import React from 'react';
import { Box, Checkbox, Chip, Stack, Typography } from '@mui/material';
import { ArrowDownward, CheckCircleOutline, HubOutlined } from '@mui/icons-material';
import { ResearchJob, ResearchWorkflowDefinition } from '../../types/research';

export default function ResearchWorkflow({ definitions, selected, onChange, job }: {
  definitions: ResearchWorkflowDefinition[]; selected: string[]; onChange?: (ids: string[]) => void; job?: ResearchJob | null;
}) {
  const latestBranch = [...(job?.events || [])].reverse().find(e => e.workflow)?.workflow;
  const visited = new Set((job?.evidence || []).filter(e => !e.error).map(e => e.workflow).filter(Boolean));
  const running = job?.status === 'running' && job.runner?.phase === 'research';
  const connector = <Box aria-hidden sx={{ display: 'flex', justifyContent: 'center', color: 'text.secondary', py: 1 }}><ArrowDownward fontSize="small" /></Box>;
  return <Box aria-label="Research workflow">
    <Stack direction="row" justifyContent="center" alignItems="center" spacing={1} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
      <CheckCircleOutline color="primary" fontSize="small" /><Typography variant="body2">Tenant allowlist → Disposable Kali runner</Typography>
    </Stack>
    {connector}
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
      {definitions.map(branch => {
        const enabled = selected.includes(branch.id);
        const state = !enabled ? 'Skipped' : running && latestBranch === branch.id ? 'Researching' : visited.has(branch.id) ? 'Evidence collected' : job?.status === 'completed' ? 'No tool evidence' : 'Selected';
        return <Box key={branch.id} sx={{ border: 1, borderColor: enabled ? 'primary.main' : 'divider', borderRadius: 2, p: 1.5,
          bgcolor: 'background.paper', position: 'relative', minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {onChange ? <Checkbox size="small" checked={enabled} inputProps={{ 'aria-label': `Research ${branch.title}` }}
              onChange={() => onChange(enabled ? selected.filter(id => id !== branch.id) : [...selected, branch.id])} /> : <HubOutlined color={enabled ? 'primary' : 'disabled'} fontSize="small" />}
            <Typography variant="subtitle2" sx={{ flex: 1 }}>{branch.title}</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>{branch.description}</Typography>
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {branch.tools.map(tool => <Chip key={tool} label={tool} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />)}
          </Stack>
          <Typography variant="caption" color={enabled ? 'text.primary' : 'text.secondary'} sx={{ display: 'block', mt: 1, fontWeight: 700 }}>{state}</Typography>
        </Box>;
      })}
    </Box>
    {connector}
    <Stack direction="row" justifyContent="center" spacing={1} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
      <Typography variant="body2">Evidence review → Scenario candidates → Excel</Typography>
    </Stack>
    {onChange && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>Uncheck a branch to exclude its research and simulation families. These are research activities; no simulation is executed.</Typography>}
  </Box>;
}
